import ConfirmationDialog, {
  ConfirmationDialogButtonColor
} from "@components/ConfirmationDialog";
import CustomSnackBar from "@components/CustomSnackBar";
import KpiBudgetCard, {
  KpiBudgetCardSkeleton
} from "@components/Scenario/KpiBudgetCard";
import KpiBudgetCharts from "@components/Scenario/KpiBudgetCharts";
import TitledChartSkeleton from "@components/TitledChartSkeleton";
import {
  INITIAL_SNACKBAR_STATE,
  ISnackbarAlert,
  setErrorAlert,
  setInitialSnackbarState
} from "@constants/alerts";
import { ROUTING_SIMULATOR } from "@constants/routing";
import { ClientContext } from "@context/Client/ClientContext";
import LayoutSelectors from "@layouts/LayoutSelectors";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  Box,
  Card,
  CardContent,
  IconButton,
  Skeleton,
  Tooltip,
  Typography
} from "@mui/material";
import {
  useClientModelInputs,
  useModelDeleteScenario,
  useModelScenario
} from "@services/api";
import { useQueryClient } from "@tanstack/react-query";
import { useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router";
import { Link } from "react-router-dom";
import ExternalVariables from "./components/ExternalVariables";
import MediaVariables from "./components/MediaVariables";

interface ConfirmationDialogStatus {
  open: boolean;
  title: string;
  buttonColor: ConfirmationDialogButtonColor;
}

const ViewScenario = () => {
  const { id } = useParams();
  const { t } = useTranslation();
  const { currentClient } = useContext(ClientContext);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [snackbarAlert, setSnackbarAlert] = useState<ISnackbarAlert>({
    ...INITIAL_SNACKBAR_STATE
  });
  const [confirmationDialogStatus, setConfirmationDialogStatus] =
    useState<ConfirmationDialogStatus>({
      open: false,
      title: "",
      buttonColor: "error"
    });

  const {
    data,
    isFetching: isLoadingScenario,
    isError: isErrorScenario
  } = useModelScenario(id ?? "");
  const deleteMutation = useModelDeleteScenario();

  const scenarioName = useMemo(() => {
    return data?.scenario.name ?? "";
  }, [data]);

  const scenarioDescription = useMemo(() => {
    return data?.scenario.description ?? "";
  }, [data]);

  const scenarioTimePeriod = useMemo(() => {
    return (data?.outputs || []).map((output) => output.time_period);
  }, [data]);

  const { data: budget, isFetching: isLoadingInflow } = useClientModelInputs(
    currentClient.model.id.toString(),
    ["budget"],
    scenarioTimePeriod
  );

  const isLoading = useMemo(
    () => isLoadingInflow || isLoadingScenario,
    [isLoadingInflow, isLoadingScenario]
  );

  const handleDeleteScenario = () => {
    setConfirmationDialogStatus({
      open: true,
      title: t("simulator.scenario.delete_scenario"),
      buttonColor: "error"
    });
  };

  const onCloseConfirmationDialog = () => {
    setConfirmationDialogStatus({
      open: false,
      title: "",
      buttonColor: "error"
    });
  };

  const onConfirmConfirmationDialog = () => {
    deleteMutation.mutate(
      {
        scenario_id: id?.toString() ?? ""
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["model", currentClient.model.id.toString(), "scenarios"]
          });
          queryClient
            .refetchQueries({
              queryKey: [
                "model",
                currentClient.model.id.toString(),
                "scenarios"
              ]
            })
            .finally(() => {
              navigate(`${ROUTING_SIMULATOR}`);
            });
        }
      }
    );
  };

  useEffect(() => {
    if (!isLoadingScenario && (isErrorScenario || data === undefined)) {
      setSnackbarAlert(
        setErrorAlert(t("simulator.errors.no_scenario_found", { id: id ?? "" }))
      );
    }
  }, [isLoadingScenario, isErrorScenario, data, id]);

  return (
    <LayoutSelectors disabled={true}>
      <Box className="w-full pt-4 pl-8 pr-8" sx={{ height: "100%" }}>
        <Box className="w-full flex pb-4">
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {t("navbar.tabs.simulator")}
          </Typography>
          <ArrowForwardIosIcon className="pt-1" />
          <Link to={ROUTING_SIMULATOR} key="scenario-to-simualtor-link">
            <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
              {t("simulator.pages.scenarios")}
            </Typography>
          </Link>
          <ArrowForwardIosIcon className="pt-1" />
          <Typography variant="h6" component="div" sx={{ fontSize: 16 }}>
            {scenarioName}
          </Typography>
        </Box>
        <Card className="w-full mt-4">
          <CardContent>
            {isLoading || data === undefined ? (
              <Skeleton variant="text" width="100%" height={100} />
            ) : (
              <Box className="w-full flex">
                <Box className="w-3/4">
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ fontSize: 16 }}
                  >
                    {scenarioName}
                  </Typography>
                  <Typography variant="h6" component="div">
                    {scenarioDescription}
                  </Typography>
                </Box>
                <Box className="w-1/4 text-right">
                  <Tooltip title={t("common.delete")}>
                    <IconButton onClick={handleDeleteScenario}>
                      <DeleteIcon color="error" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>
            )}
          </CardContent>
        </Card>

        <Box className="w-full mt-4 pb-8">
          {isLoading || data === undefined ? (
            <KpiBudgetCardSkeleton />
          ) : (
            <KpiBudgetCard
              timePeriods={scenarioTimePeriod}
              outputs={data?.outputs || []}
              budget={budget || []}
            />
          )}
          {isLoading || data === undefined ? (
            <TitledChartSkeleton className="w-full" />
          ) : (
            <KpiBudgetCharts
              timePeriods={scenarioTimePeriod}
              outputs={data?.outputs || []}
              budget={budget || []}
              media_investment={data?.scenario.media_investment || 0}
              scenarioName={scenarioName}
            />
          )}

          <ExternalVariables
            inputs={data?.inputs || []}
            timePeriods={scenarioTimePeriod}
          />

          <MediaVariables
            inputs={data?.inputs || []}
            timePeriods={scenarioTimePeriod}
          />
        </Box>
        <ConfirmationDialog
          open={confirmationDialogStatus.open}
          onClose={onCloseConfirmationDialog}
          onConfirm={onConfirmConfirmationDialog}
          title={confirmationDialogStatus.title}
          buttonColor={confirmationDialogStatus.buttonColor}
        />
        <CustomSnackBar
          id={`scenario-${id}-snack-bar`}
          open={snackbarAlert.open}
          handleClose={() => setSnackbarAlert(setInitialSnackbarState())}
          color={snackbarAlert.severity}
          message={snackbarAlert.message}
          autoHideDuration={snackbarAlert.autoHideDuration}
        />
      </Box>
    </LayoutSelectors>
  );
};

export default ViewScenario;
